import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Timer, LogOut, AlertTriangle, Plus, ImagePlus, X } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useLang } from "@/lib/i18n";
import { api, errDetail, fmtDate } from "@/lib/api";
import { StatusBadge } from "./Lists";
import { IncidentPhotos } from "./IncidentPhotos";

const CL = ["saved", "laptop", "equipment", "garbage", "damage", "guests", "door"];
const fmtLeft = (ms) => { const s = Math.max(0, Math.floor(ms / 1000)); return `${Math.floor(s / 3600)}:${String(Math.floor((s % 3600) / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`; };

export function OnboardingPanel({ status, onChange }) {
  const { t, tErr } = useLang();
  const [d, setD] = useState(null);
  const [slot, setSlot] = useState("");
  const load = useCallback(() => api.get("/member/onboarding").then((r) => setD(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const req = async () => {
    try { await api.post("/member/onboarding/request", { slot }); toast.success(t("x.onbSent")); load(); onChange(); }
    catch (e) { toast.error(tErr(errDetail(e))); }
  };
  const canPick = ["not_started", "appointment_pending", "appointment_confirmed"].includes(status);
  return (
    <div className="panel brackets mb-10 p-5 sm:p-6" data-testid="onboarding-panel">
      <div className="mb-3 flex flex-wrap items-center gap-3"><h2 className="font-display text-3xl uppercase text-white">{t("x.onbTitle")}</h2><StatusBadge s={status} /></div>
      <p className="text-sm text-slate-300">{t("x.onbIntro")}</p>
      <p className="mt-2 rounded-lg border border-amber-500/30 bg-amber-950/20 p-3 text-sm text-amber-200" data-testid="onboarding-id-note">{t("x.onbId")}</p>
      {d?.onboarding?.appointment && <p className="mt-3 font-mono text-sm text-white" data-testid="onboarding-appointment">{d.onboarding.appointment} (MTL)</p>}
      {canPick && d && (
        <div className="mt-4">
          <p className="mb-2 text-xs uppercase tracking-widest text-slate-500">{t("x.onbPick")}</p>
          {!d.slots.length ? <p className="text-sm text-slate-500">{t("x.onbNone")}</p> : (
            <div className="flex flex-wrap gap-2">
              {d.slots.map((s, i) => <button key={s} onClick={() => setSlot(s)} data-testid={`onboarding-slot-${i}`} className={`rounded-lg border px-3 py-2 font-mono text-sm ${slot === s ? "border-red-500 bg-red-600 text-white" : "border-white/10 text-slate-200"}`}>{s}</button>)}
            </div>
          )}
          <button disabled={!slot} onClick={req} data-testid="onboarding-request-btn" className="btn-red mt-4 rounded-full px-5 py-2.5 text-sm font-semibold disabled:opacity-40">{t("x.onbRequest")}</button>
        </div>
      )}
      <p className="mt-4 text-xs text-slate-500">{t("x.onbBlock")}</p>
    </div>
  );
}

function CheckoutDialog({ b, open, setOpen, onDone }) {
  const { t, tErr } = useLang();
  const [c, setC] = useState({});
  const [note, setNote] = useState("");
  const go = async () => {
    try { await api.post(`/member/bookings/${b.id}/checkout`, { checklist: c, note }); toast.success(t("x.checkoutDone")); setOpen(false); onDone(); }
    catch (e) { toast.error(tErr(errDetail(e))); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-white/10 bg-[#0f1015]" aria-describedby={undefined} data-testid="checkout-dialog">
        <DialogHeader><DialogTitle>{t("x.checkout")}</DialogTitle></DialogHeader>
        <p className="flex gap-2 rounded-lg border border-red-500/40 bg-red-950/40 p-3 text-sm text-red-200" data-testid="checkout-emergency"><AlertTriangle size={18} className="shrink-0" />{t("x.emergency")}</p>
        <div className="space-y-3">
          {CL.map((k) => (
            <label key={k} className="flex cursor-pointer items-start gap-3 text-sm text-slate-200">
              <Checkbox checked={!!c[k]} onCheckedChange={(v) => setC({ ...c, [k]: !!v })} data-testid={`checklist-${k}`} />{t(`x.cl_${k}`)}
            </label>
          ))}
        </div>
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note" className="border-white/15 bg-black/40" />
        <button disabled={!CL.every((k) => c[k])} onClick={go} data-testid="checkout-submit-btn" className="btn-red h-11 rounded-full font-semibold disabled:opacity-40">{t("x.checkout")}</button>
      </DialogContent>
    </Dialog>
  );
}

const MAX_PHOTOS = 3, MAX_MB = 10;

function PhotoPicker({ files, setFiles }) {
  const { t } = useLang();
  const add = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    if (picked.some((f) => f.size > MAX_MB * 1024 * 1024)) toast.error(t("err.file_too_large"));
    const ok = picked.filter((f) => f.size <= MAX_MB * 1024 * 1024 && /^image\/(jpeg|png|webp)$/.test(f.type));
    if (files.length + ok.length > MAX_PHOTOS) toast.error(t("err.too_many_photos"));
    setFiles([...files, ...ok].slice(0, MAX_PHOTOS));
  };
  return (
    <div className="mt-2">
      <div className="flex flex-wrap gap-2">
        {files.map((f, i) => (
          <span key={i} className="relative h-16 w-16 overflow-hidden rounded-lg border border-white/10" data-testid={`incident-preview-${i}`}>
            <img src={URL.createObjectURL(f)} alt="" className="h-full w-full object-cover" />
            <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))} data-testid={`incident-remove-photo-${i}`} className="absolute right-0.5 top-0.5 rounded-full bg-black/70 p-0.5 text-white"><X size={12} /></button>
          </span>
        ))}
        {files.length < MAX_PHOTOS && (
          <label className="flex h-16 w-16 cursor-pointer items-center justify-center rounded-lg border border-dashed border-white/20 text-slate-400 hover:border-red-500 hover:text-white" data-testid="incident-add-photo">
            <ImagePlus size={20} /><input type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={add} data-testid="incident-photo-input" />
          </label>
        )}
      </div>
      <p className="mt-1 text-xs text-slate-500" data-testid="incident-photo-note">{t("x.incidentPhotos")}</p>
    </div>
  );
}

function IncidentBox({ bookingId }) {
  const { t, tErr } = useLang();
  const [txt, setTxt] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [mine, setMine] = useState([]);
  const load = useCallback(() => api.get("/member/incidents").then((r) => setMine(r.data.filter((i) => i.booking_id === bookingId))), [bookingId]);
  useEffect(() => { load(); }, [load]);
  const send = async () => {
    const fd = new FormData();
    fd.append("description", txt);
    fd.append("booking_id", bookingId || "");
    files.forEach((f) => fd.append("files", f));
    setBusy(true);
    try { await api.post("/member/incidents", fd); toast.success(t("x.incidentOk")); setTxt(""); setFiles([]); load(); }
    catch (e) { toast.error(tErr(errDetail(e))); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-4" data-testid="incident-box">
      <p className="mb-2 text-xs uppercase tracking-widest text-slate-500">{t("x.incident")}</p>
      <Textarea value={txt} onChange={(e) => setTxt(e.target.value)} placeholder={t("x.incidentPh")} className="border-white/15 bg-black/40" data-testid="incident-input" />
      <PhotoPicker files={files} setFiles={setFiles} />
      <button disabled={busy || txt.trim().length < 3} onClick={send} data-testid="incident-submit-btn" className="btn-chrome mt-2 rounded-full px-4 py-2 text-sm disabled:opacity-40">{busy ? "…" : t("x.send")}</button>
      {mine.length > 0 && (
        <ul className="mt-4 space-y-2" data-testid="my-incidents">{mine.map((i) => (
          <li key={i.id} className="rounded-lg border border-white/10 p-3 text-sm text-slate-300" data-testid={`my-incident-${i.id}`}><p>{i.description}</p><IncidentPhotos inc={i} /></li>
        ))}</ul>
      )}
    </div>
  );
}

export function SessionPanel({ onChange }) {
  const { t, lang, tErr } = useLang();
  const [s, setS] = useState(null);
  const [skew, setSkew] = useState(0);
  const [tick, setTick] = useState(Date.now());
  const [open, setOpen] = useState(false);
  const [ext, setExt] = useState(0.5);
  const load = useCallback(() => api.get("/member/session").then((r) => { setS(r.data.session); if (r.data.session) setSkew(new Date(r.data.session.server_now) - Date.now()); }), []);
  useEffect(() => { load(); const i = setInterval(() => setTick(Date.now()), 1000); const j = setInterval(load, 60000); return () => { clearInterval(i); clearInterval(j); }; }, [load]);
  if (!s) return null;
  const nowMs = tick + skew;
  const start = new Date(s.start).getTime(), end = new Date(s.end).getTime();
  const active = nowMs >= start && nowMs < end;
  const started = nowMs >= start;
  const extend = async () => {
    try { await api.post(`/member/bookings/${s.id}/extend`, { extra_hours: ext }); toast.success(t("x.extendOk")); load(); onChange(); }
    catch (e) { toast.error(tErr(errDetail(e))); }
  };
  return (
    <div className={`panel mb-10 p-5 sm:p-6 ${active ? "border-red-500/60" : ""}`} data-testid="session-panel">
      <p className="eyebrow mb-2 flex items-center gap-2">{active && <span className="rec-dot" />}{t("x.next")}</p>
      <p className="text-lg capitalize text-white" data-testid="session-times">{fmtDate(s.local_date, lang)} · <span className="font-mono">{s.local_start}–{s.local_end}</span></p>
      <p className="mt-3 flex items-center gap-2 font-mono text-4xl text-white" data-testid="session-countdown"><Timer className="text-red-500" />{fmtLeft(active ? end - nowMs : start - nowMs)}</p>
      <p className="text-xs text-slate-500">{active ? t("x.left") : started ? "" : t("x.startsIn")}</p>
      {started && (
        <button onClick={() => setOpen(true)} data-testid="finish-session-btn" className="btn-red mt-5 flex h-14 w-full items-center justify-center gap-2 rounded-full text-lg font-semibold sm:w-auto sm:px-10"><LogOut size={20} />{t("x.checkout")}</button>
      )}
      {active && (
        <div className="mt-5 flex flex-wrap items-center gap-2" data-testid="extend-box">
          <span className="text-sm text-slate-400">{t("x.extendQ")}</span>
          <select value={ext} onChange={(e) => setExt(Number(e.target.value))} className="h-10 rounded-lg border border-white/15 bg-black/40 px-2 text-white" data-testid="extend-hours-select">
            {[0.5, 1, 1.5, 2, 3].map((h) => <option key={h} value={h}>{h} h</option>)}
          </select>
          <span className="text-sm text-slate-400" data-testid="extend-deduct">{t("x.extendDeduct")}: <b className="text-white">{ext} h</b></span>
          <button onClick={extend} data-testid="extend-submit-btn" className="btn-chrome inline-flex items-center gap-1 rounded-full px-4 py-2 text-sm"><Plus size={14} />{t("x.extend")}</button>
        </div>
      )}
      {started && <IncidentBox bookingId={s.id} />}
      <CheckoutDialog b={s} open={open} setOpen={setOpen} onDone={() => { load(); onChange(); }} />
    </div>
  );
}

export function EquipmentInfo({ eq }) {
  const { t, lang } = useLang();
  const rows = [["eqModel", eq?.model], ["eqCables", eq?.cables], ["eqDrivers", eq?.drivers], ["eqSetup", eq?.[`setup_${lang}`]]];
  return (
    <div className="panel mb-10 p-5" data-testid="equipment-info">
      <p className="eyebrow mb-3">{t("x.equipment")}</p>
      <dl className="grid gap-3 sm:grid-cols-2">{rows.map(([k, v]) => <div key={k}><dt className="text-xs text-slate-500">{t(`x.${k}`)}</dt><dd className="whitespace-pre-line text-sm text-slate-200">{v || t("x.eqTbd")}</dd></div>)}</dl>
    </div>
  );
}

export function TermsGate({ version, onDone }) {
  const { t, tErr } = useLang();
  const [ok, setOk] = useState(false);
  const accept = async () => { try { await api.post("/member/accept-terms", { version }); onDone(); } catch (e) { toast.error(tErr(errDetail(e))); } };
  return (
    <div className="panel brackets mb-10 p-5" data-testid="terms-gate">
      <p className="mb-3 text-white">{t("x.termsUpdated")}</p>
      <TermsCheck ok={ok} setOk={setOk} />
      <button disabled={!ok} onClick={accept} data-testid="terms-accept-btn" className="btn-red mt-4 rounded-full px-5 py-2.5 text-sm font-semibold disabled:opacity-40">{t("x.accept")}</button>
    </div>
  );
}

export function TermsCheck({ ok, setOk, id = "terms-accept-checkbox" }) {
  const { t } = useLang();
  return (
    <label className="flex cursor-pointer items-start gap-3 text-sm text-slate-200">
      <Checkbox checked={ok} onCheckedChange={(v) => setOk(!!v)} data-testid={id} />
      <span>{t("x.acceptTerms")} — <Link to="/terms" target="_blank" className="text-red-400 underline">{t("footer.terms")}</Link> · <Link to="/privacy" target="_blank" className="text-red-400 underline">{t("footer.privacy")}</Link></span>
    </label>
  );
}
